import React, { useState, useEffect } from "react";
import * as ReactDOM from "react-dom";
import {
    Text,
    Stack,
    TextField,
    PrimaryButton,
    MessageBar,
    MessageBarType,
    Link,
    mergeStyleSets,
} from "@fluentui/react";

export const AUTH_TOKEN_KEY = "modaq_auth_token";
export const ADMIN_AUTH_TOKEN_KEY = "modaq_admin_auth_token";

const loginStyles = mergeStyleSets({
    container: {
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        fontFamily: "Segoe UI, sans-serif",
    },
    disclaimer: {
        marginBottom: 16,
        textAlign: "center" as const,
    },
    formCard: {
        padding: 32,
        border: "1px solid #e1e1e1",
        borderRadius: 8,
        maxWidth: 300,
        width: "100%",
        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
        backgroundColor: "#ffffff",
    },
    title: {
        marginBottom: 16,
    },
});

interface ILoginFormProps {
    onSuccess: () => void;
    /** sets whether the login is for the admin page
     * maybe at some point have accounts and roles/permissions
     */
    adminMode?: boolean;
}

export function LoginForm({ onSuccess, adminMode }: ILoginFormProps) {
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError("");
        setLoading(true);

        try {
            const response = await fetch(`/api/${adminMode ? "admin-login" : "login"}`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ password }),
            });

            const data = await response.json();

            if (response.ok && data.success) {
                localStorage.setItem(adminMode ? ADMIN_AUTH_TOKEN_KEY : AUTH_TOKEN_KEY, data.token);
                onSuccess();
            } else {
                setError(data.error || "Invalid password");
            }
        } catch (err) {
            setError("Login failed. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className={loginStyles.container}>
            <Text variant="medium" className={loginStyles.disclaimer}>
                If you're looking for live stats, please visit the <Link href="/scoreboard">scoreboard</Link>.
            </Text>
            <form onSubmit={handleSubmit} className={loginStyles.formCard}>
                <Stack tokens={{ childrenGap: 16 }}>
                    <Text variant="xLarge" block className={loginStyles.title}>
                        {adminMode ? "Admin Login" : "Moderator Login"}
                    </Text>
                    <TextField
                        type="password"
                        value={password}
                        onChange={(_, newValue) => setPassword(newValue || "")}
                        placeholder="Enter password"
                        autoFocus
                        canRevealPassword
                    />
                    {error && <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>}
                    <PrimaryButton type="submit" disabled={loading} text={loading ? "Logging in..." : "Login"} />
                </Stack>
            </form>
        </div>
    );
}